import { bundleMemberGuids } from '@domainUtils'
import dayjs from 'dayjs'
import { reject, reverse, sortBy, toNumber } from 'lodash-es'
import { storeyToLabel } from '@editor/pages/Plans/components/AddPlanDialog/components/utils'
import { roofStoreyKey } from '@editorStores'
import type { PlanElementType, PlanViewTab } from '@editorStores'
import { todayStringDE } from '@utils'
import {
  DEFAULT_PLAN_VIEW_PLAN_TYPE,
  PLAN_VIEW_PLAN_ELEMENT_TYPE_BY_TAB,
  PLAN_VIEW_PLAN_TYPES,
  PlanViewPlanChoice,
  PlanViewPlanElementTab,
  PlanViewPlanType,
} from './constants'

export type PositionGroupingsByType = Partial<
  Record<PositionGroupingType, MemberPositionBundle[] | null | undefined>
>

/** Narrows the global comment filter to a plan the plan view can preview. */
export const toPlanViewPlanType = (plan: string | null | undefined): PlanViewPlanType =>
  PLAN_VIEW_PLAN_TYPES.find(planType => planType === plan) ?? DEFAULT_PLAN_VIEW_PLAN_TYPE

/** Topmost regular (non-roof) storey, or null when there is none. */
export const getTopRegularStorey = (storeys: Iterable<string>): string | null => {
  const regular = [...storeys].filter(storey => storey !== roofStoreyKey)
  if (!regular.length) return null
  return regular.reduce((top, storey) => (toNumber(storey) > toNumber(top) ? storey : top))
}

/**
 * Storey the plan view previews: the requested one when it exists, otherwise the ground floor
 * (or the lowest available storey). The roof can be previewed on the position plan (drawn on the
 * top storey's floorplan with only the roof elements visible); the stiffening plan never contains
 * the roof, so it falls back to the topmost regular storey there.
 */
export const resolvePlanViewStorey = (
  requested: string | null | undefined,
  storeys: readonly string[],
  planType: PlanViewPlanType,
): string | null => {
  const allowed =
    planType === 'stiffeningPlan' ? storeys.filter(storey => storey !== roofStoreyKey) : storeys
  if (!allowed.length) return null
  if (requested && allowed.includes(requested)) return requested
  if (requested === roofStoreyKey) return getTopRegularStorey(allowed)
  return allowed.includes('0') ? '0' : allowed[0]
}

/**
 * Storeys the plan view previews: the requested ones that exist. The position plan can combine
 * several storeys (e.g. "Dach" + the top storey, like "Dach + weitere" on the plans page); the
 * stiffening plan always shows exactly one regular storey — the topmost requested one (the roof
 * falls back to the topmost regular storey there). Falls back to a single default storey (see
 * `resolvePlanViewStorey`) when none of the requested storeys is available.
 */
export const resolvePlanViewStoreys = (
  requested: readonly string[],
  storeys: readonly string[],
  planType: PlanViewPlanType,
): string[] => {
  if (planType === 'positionPlan') {
    const kept = requested.filter(storey => storeys.includes(storey))
    if (kept.length) return sortStoreysForSelect(kept)
  }
  const single = resolvePlanViewStorey(
    getTopRegularStorey(requested) ?? requested[0] ?? null,
    storeys,
    planType,
  )
  return single === null ? [] : [single]
}

/** Storey select order: roof first, then the regular storeys descending. */
export const sortStoreysForSelect = (storeys: Iterable<string>): string[] => {
  const all = [...storeys]
  const regular = reverse(
    sortBy(
      reject(all, storey => storey === roofStoreyKey),
      storey => toNumber(storey),
    ),
  )
  return all.includes(roofStoreyKey) ? [roofStoreyKey, ...regular] : regular
}

/**
 * Storeys currently previewed, derived from the scene state the preset wrote: the visible storeys
 * (the preset makes exactly the previewed storeys visible), or the floorplan storey when none are.
 */
export const getPlanViewStoreys = (
  floorplanStorey: string | null,
  visibleStoreys: ReadonlySet<string>,
): string[] => {
  if (visibleStoreys.size) return sortStoreysForSelect(visibleStoreys)
  return floorplanStorey === null ? [] : [floorplanStorey]
}

/**
 * Storeys the export of the previewed plan covers (what `useGeneratePlan` sends): every previewed
 * storey for the position plan, only the first one for the stiffening plan.
 */
export const getPlanViewExportStoreys = (
  planType: PlanViewPlanType,
  previewedStoreys: readonly string[],
): string[] =>
  planType === 'stiffeningPlan' ? previewedStoreys.slice(0, 1) : [...previewedStoreys]

/**
 * Storey currently previewed, derived from the scene state the preset wrote: the roof when only
 * the roof storey is visible, otherwise the active floorplan storey.
 */
export const getPlanViewStorey = (
  floorplanStorey: string | null,
  visibleStoreys: ReadonlySet<string>,
): string | null =>
  visibleStoreys.size === 1 && visibleStoreys.has(roofStoreyKey) ? roofStoreyKey : floorplanStorey

/** `exported` flag per member guid (representative and aggregated members alike). */
const exportedByGuid = (
  bundles: MemberPositionBundle[] | null | undefined,
): Map<string, boolean> => {
  const lookup = new Map<string, boolean>()
  bundles?.forEach(bundle => {
    const exported = Boolean(bundle.exported)
    bundleMemberGuids(bundle).forEach(guid => lookup.set(guid, exported))
  })
  return lookup
}

/**
 * Guids of the positions the position plan export leaves out: members whose position bundle is
 * not exported. Slab surfaces (Decke/Dach positions) have no bundle of their own — they follow
 * their slab beam's bundle, so the surface and its beam are hidden together. Members without a
 * bundle stay visible (they carry no grouping the export could skip).
 */
export const computeNonExportedPositionGuids = (
  model: PlanarModel,
  groupings: PositionGroupingsByType,
): string[] => {
  const hidden: string[] = []

  const collect = (
    guids: readonly string[],
    bundles: MemberPositionBundle[] | null | undefined,
  ) => {
    const lookup = exportedByGuid(bundles)
    guids.forEach(guid => {
      if (lookup.get(guid) === false) hidden.push(guid)
    })
  }

  collect(
    model.beams.map(beam => beam.guid),
    groupings.beams,
  )
  collect(
    model.columns.map(column => column.guid),
    groupings.columns,
  )
  collect(
    model.purlins.map(purlin => purlin.guid),
    groupings.purlins,
  )
  collect(
    model.rips.map(rip => rip.position_guid),
    groupings['wall-rips'],
  )
  collect(
    model.lintels.map(lintel => lintel.position_guid),
    groupings['wall-lintels'],
  )

  const collectSlabSurfaces = (
    surfaces: readonly (VerticalSlab | VerticalRoofSlab)[],
    bundles: MemberPositionBundle[] | null | undefined,
  ) => {
    const lookup = exportedByGuid(bundles)
    surfaces.forEach(surface => {
      const beamGuid = surface.beam?.guid
      if (!beamGuid || lookup.get(beamGuid) !== false) return
      hidden.push(surface.guid, beamGuid)
    })
  }

  collectSlabSurfaces(model.vertical_slabs, groupings['slab-beams'])
  collectSlabSurfaces(model.vertical_roof_slabs, groupings['roof-slab-beams'])

  return hidden
}

/**
 * File name a saved plan is downloaded under: its name followed by the project name, with a `.pdf`
 * extension (`Positionsplan-EG - Projekt.pdf`, as the old Pläne page's download names it). The
 * PDF viewer the plan was optimised for stays out of it: the engineers send plans out under their
 * name.
 */
export const getPlanPdfFileName = (plan: Pick<Plan, 'node_name'>, projectName?: string): string => {
  const planName = (plan.node_name.trim() || 'Plan').replace(/\.pdf$/i, '')
  const project = projectName?.trim()
  return `${project ? `${planName} - ${project}` : planName}.pdf`
}

/**
 * Name a generated plan is saved under unless the user enters another one; matches the backend's
 * defaults (`Positionsplan-EG+1. OG+Dach`, `Aussteifungsplan-EG`, `Lastenplan`).
 */
export const getDefaultPlanName = (
  planChoice: PlanViewPlanChoice,
  storeys: readonly string[],
): string => {
  if (planChoice === 'loadPlan') return 'Lastenplan'
  if (planChoice === 'stiffeningPlan') {
    return storeys.length ? `Aussteifungsplan-${storeyToLabel(storeys[0])}` : 'Aussteifungsplan'
  }
  // Numeric storeys ascending, the roof last.
  const regular = sortBy(
    reject(storeys, storey => storey === roofStoreyKey),
    toNumber,
  )
  const labels = [
    ...regular.map(storeyToLabel),
    ...(storeys.includes(roofStoreyKey) ? [storeyToLabel(roofStoreyKey)] : []),
  ]
  return labels.length ? `Positionsplan-${labels.join('+')}` : 'Positionsplan'
}

/** Today as the plans' hand-in date (`DD-MM-YYYY`), also a generated plan's default comment. */
export const getTodayHandInDate = (): string =>
  dayjs(todayStringDE(), 'DD.MM.YYYY').format('DD-MM-YYYY')

export const isPlanViewPlanElementTab = (tab: PlanViewTab): tab is PlanViewPlanElementTab =>
  tab in PLAN_VIEW_PLAN_ELEMENT_TYPE_BY_TAB

/** The tab listing `planElementType`, if a plan element tab lists it. */
export const getPlanViewPlanElementTab = (
  planElementType: PlanElementType | null,
): PlanViewPlanElementTab | null =>
  (Object.keys(PLAN_VIEW_PLAN_ELEMENT_TYPE_BY_TAB) as PlanViewPlanElementTab[]).find(
    tab => PLAN_VIEW_PLAN_ELEMENT_TYPE_BY_TAB[tab] === planElementType,
  ) ?? null

/**
 * The sidebar tab the plan view shows: the selected plan element's tab, so selecting a table,
 * comment or image card in the scene opens its form, else the chosen tab. "Pläne" covers the
 * scene, so nothing can be selected there and it is always kept.
 */
export const getDisplayedPlanViewTab = (
  tab: PlanViewTab,
  planElementType: PlanElementType | null,
): PlanViewTab => {
  if (tab === 'plans') return tab
  return getPlanViewPlanElementTab(planElementType) ?? tab
}
